/**
 * Period windows, anchored to UTC calendar dates.
 *
 * An entry date is a calendar date, not a moment — "August 1" means the same
 * thing to everyone looking at the dealership's numbers. Those dates are stored
 * as UTC timestamps, so which period a row belongs to has to be decided from
 * its UTC components. Reading them with local getters shifts every row by the
 * viewer's offset, which drops the 1st of the month for anyone west of UTC and
 * quietly pulls the next month's 1st in to replace it.
 *
 * The reference period — which day, week, month or year you're looking at —
 * still comes from the user's local clock or their date picker. Only row
 * membership is UTC. Getting that backwards would show a Pacific user September
 * on the afternoon of August 31.
 *
 * The backend builds the same boundaries with Date.UTC in
 * profitSummaryController. The two must agree, or the same month reads
 * differently on Profit Summary than it does on Overview.
 */

const pad = (n) => String(n).padStart(2, '0');

/** A row's calendar date as YYYY-MM-DD, read from its UTC components. */
export const utcDateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** A row's calendar month as YYYY-MM. */
export const utcMonthKey = (value) => utcDateKey(value).slice(0, 7);

/** A row's calendar year as YYYY. */
export const utcYearKey = (value) => utcDateKey(value).slice(0, 4);

/**
 * The date a week begins, as YYYY-MM-DD, given any YYYY-MM-DD key.
 * `weekStartsOn` is 0 for Sunday (the fixed ops convention) or 1 for Monday
 * (the Overview convention); callers pass whichever their surface already used,
 * so this changes timezone handling without quietly redefining anyone's week.
 */
export const weekKeyOf = (dateKey, weekStartsOn = 0) => {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '';
  const shift = (d.getUTCDay() - weekStartsOn + 7) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return utcDateKey(d);
};

/** The week a row belongs to. */
export const utcWeekKey = (value, weekStartsOn = 0) => weekKeyOf(utcDateKey(value), weekStartsOn);

/** Local calendar parts of a reference date — what the *viewer* means by "now". */
const localParts = (ref) => {
  const d = ref instanceof Date ? ref : new Date(ref);
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
};

/**
 * The reference side of a comparison: the calendar date the viewer means, read
 * from their local clock or picker. Never use utcDateKey for this — at 5pm in
 * Los Angeles the UTC date is already tomorrow, and "today" would skip a day.
 */
export const localDateKey = (ref = new Date()) => {
  const { year, month, day } = localParts(ref);
  return `${year}-${pad(month + 1)}-${pad(day)}`;
};

/** The calendar month the viewer means, as YYYY-MM. */
export const localMonthKey = (ref = new Date()) => localDateKey(ref).slice(0, 7);

/** The calendar year the viewer means, as YYYY. */
export const localYearKey = (ref = new Date()) => localDateKey(ref).slice(0, 4);

const startISO = (y, m, d) => new Date(Date.UTC(y, m, d, 0, 0, 0, 0)).toISOString();
const endISO = (y, m, d) => new Date(Date.UTC(y, m, d, 23, 59, 59, 999)).toISOString();

/** The UTC instants spanning one local calendar day. */
export const dayWindowUTC = (ref = new Date()) => {
  const { year, month, day } = localParts(ref);
  return { startDate: startISO(year, month, day), endDate: endISO(year, month, day) };
};

/** The UTC instants spanning the week containing `ref`. */
export const weekWindowUTC = (ref = new Date(), weekStartsOn = 0) => {
  const { year, month, day } = localParts(ref);
  const anchor = new Date(Date.UTC(year, month, day));
  const shift = (anchor.getUTCDay() - weekStartsOn + 7) % 7;
  anchor.setUTCDate(anchor.getUTCDate() - shift);
  const end = new Date(anchor);
  end.setUTCDate(end.getUTCDate() + 6);
  return {
    startDate: startISO(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate()),
    endDate: endISO(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  };
};

/** The UTC instants spanning the whole calendar month containing `ref`. */
export const monthWindowUTC = (ref = new Date()) => {
  const { year, month } = localParts(ref);
  return { startDate: startISO(year, month, 1), endDate: endISO(year, month + 1, 0) };
};

/** The UTC instants spanning two local calendar dates, inclusive of both. */
export const rangeWindowUTC = (from, to) => {
  const a = localParts(from);
  const b = localParts(to);
  return { startDate: startISO(a.year, a.month, a.day), endDate: endISO(b.year, b.month, b.day) };
};
